import { v4 as uuid } from 'uuid';

import { ChatbotType } from 'apis/chatbots/types/chatbots.types';
import { flowNodeColor } from 'fable/flowEditor';
import type { MessengerExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';
import { useChatBots } from 'hooks/useChatBots';

import { ChatbotModal } from '../chatBotModal';

/** This payload represents the data that Backend expects/sends */
interface ChatBotPayload {
  chatbotId: string;
  chatbotName: string;
  additionalSettings?: {
    chatbotType?: ChatbotType;
  }
  successNextNodeId: string;
  failNextNodeId: string;
}

type ChatBotNodeAttributes = GenericNodeAttributes<
  ChatBotPayload,
  MessengerExtraData
>;

type ChatBotNodeProps = GenericNodeProps<ChatBotPayload, MessengerExtraData>;

const connectionLabels = {
  successBranch: 'Successful',
  failureBranch: 'Not succeeded',
} as const;

const translatePayload: ChatBotNodeAttributes['translatePayload'] = ({
  payload,
}) => {
  const nextNodes =
    payload.additionalSettings?.chatbotType == 'mim'
      ? [
          {
            id: uuid(),
            label: connectionLabels.failureBranch,
            connection: payload.failNextNodeId,
          },
        ]
      : [
          {
            id: uuid(),
            label: connectionLabels.successBranch,
            connection: payload.successNextNodeId,
          },
          {
            id: uuid(),
            label: connectionLabels.failureBranch,
            connection: payload.failNextNodeId,
          },
        ];
  return nextNodes;
};

const updateConnection: ChatBotNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const { payload } = node;
  const fieldToUpdate: Exclude<keyof ChatBotPayload, 'query'> =
    connection.connectionLabel === connectionLabels.successBranch
      ? 'successNextNodeId'
      : 'failNextNodeId';

  return {
    ...node,
    payload: {
      ...payload,
      [fieldToUpdate]: connectionId,
    },
  };
};

export const ChatBotNodeAttributes: ChatBotNodeAttributes = {
  iconName: 'chat-bot',
  label: 'Chatbot',
  color: hexStringToNumber(flowNodeColor.blue),
  translatePayload,
  updateConnection,
  NodeContent: AvailabilityNode,
  ActionModal: ChatbotModal,
};

export function AvailabilityNode({ node }: ChatBotNodeProps): JSX.Element {
  const { items } = useChatBots();

  const {
    payload: { chatbotId },
  } = node;

  const name =
    items?.find((item) => item.id === chatbotId)?.name ?? 'Loading...';

  return (
    <TextContent>
      <Text weight="semibold" text={name} />
    </TextContent>
  );
}
